import { useState, useRef } from 'react';
import { uploadApi } from '../../api/client';
import type { NavbarLogo } from '../../schemas/validation';

// Baseline logo dimension constraints for navbar image logos.
const LOGO_CONSTRAINTS = {
  minHeight: 24,
  maxHeight: 96,
  minWidth: 80,
  maxWidth: 320,
};

export type LogoDimensionCaps = {
  minHeight: number;
  maxHeight: number;
  minWidth: number;
  maxWidth: number;
};

export type LogoDimensionCapsMode = 'text' | 'image' | 'legacy-both' | undefined;

/** Maps stored navbar logo mode (including legacy "both") to dimension cap mode. */
export function toLogoDimensionCapsMode(
  mode: NavbarLogo['mode'] | undefined
): LogoDimensionCapsMode {
  if (mode === 'both') return 'legacy-both';
  if (mode === 'text') return 'image';
  return mode;
}

/** Caps used by the navbar on the live site (must stay in sync with NavbarComponent / API layoutRenderers). */
export function getLogoDimensionCapsForMode(mode: LogoDimensionCapsMode): LogoDimensionCaps {
  const isLegacyBoth = mode === 'legacy-both';
  return {
    minHeight: LOGO_CONSTRAINTS.minHeight,
    minWidth: LOGO_CONSTRAINTS.minWidth,
    maxHeight: isLegacyBoth ? 40 : LOGO_CONSTRAINTS.maxHeight,
    maxWidth: isLegacyBoth ? 120 : LOGO_CONSTRAINTS.maxWidth,
  };
}

/** Clamped width/height and max-* for <img style> — same math as NavbarComponent. */
export function clampLogoImageSizeForRender(
  imageWidth: number | undefined,
  imageHeight: number | undefined,
  mode: LogoDimensionCapsMode
): { width: number; height: number; maxWidth: number; maxHeight: number } {
  const caps = getLogoDimensionCapsForMode(mode === 'text' ? 'image' : mode);
  const height = Math.min(Math.max(imageHeight ?? 48, caps.minHeight), caps.maxHeight);
  const width = Math.min(Math.max(imageWidth ?? 120, caps.minWidth), caps.maxWidth);
  return { width, height, maxWidth: caps.maxWidth, maxHeight: caps.maxHeight };
}

// Helper to get image dimensions from a file
const getImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
      URL.revokeObjectURL(img.src);
    };
    img.onerror = () => {
      reject(new Error('Failed to load image'));
      URL.revokeObjectURL(img.src);
    };
    img.src = URL.createObjectURL(file);
  });
};

// Calculate constrained dimensions maintaining aspect ratio
export const calculateLogoDimensions = (
  originalWidth: number,
  originalHeight: number,
  targetHeight: number,
  lockAspectRatio: boolean = true,
  caps: LogoDimensionCaps = getLogoDimensionCapsForMode('image')
): { width: number; height: number } => {
  const clampedHeight = Math.min(Math.max(targetHeight, caps.minHeight), caps.maxHeight);

  if (!lockAspectRatio) {
    return { width: caps.maxWidth, height: clampedHeight };
  }

  const aspectRatio = originalWidth / originalHeight;
  let computedWidth = Math.round(clampedHeight * aspectRatio);

  computedWidth = Math.min(Math.max(computedWidth, caps.minWidth), caps.maxWidth);

  let finalHeight = clampedHeight;
  if (computedWidth === caps.maxWidth && clampedHeight * aspectRatio > caps.maxWidth) {
    finalHeight = Math.round(computedWidth / aspectRatio);
    finalHeight = Math.min(Math.max(finalHeight, caps.minHeight), caps.maxHeight);
  }

  return { width: computedWidth, height: finalHeight };
};

export { LOGO_CONSTRAINTS };

interface UseLogoUploadReturn {
  isUploading: boolean;
  uploadError: string | null;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  handleFileSelect: (
    event: React.ChangeEvent<HTMLInputElement>,
    siteId: string,
    logoData: Partial<NavbarLogo>,
    onSuccess: (result: Partial<NavbarLogo>) => void
  ) => Promise<void>;
  setUploadError: (error: string | null) => void;
  clearError: () => void;
}

export function useLogoUpload(): UseLogoUploadReturn {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>,
    siteId: string,
    logoData: Partial<NavbarLogo>,
    onSuccess: (result: Partial<NavbarLogo>) => void
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setUploadError('Please select a valid image file (JPG, PNG, SVG, or WebP).');
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      setUploadError('File size must be less than 10MB.');
      return;
    }

    try {
      setIsUploading(true);
      setUploadError(null);

      // Validate siteId format
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!siteId || !uuidRegex.test(siteId)) {
        throw new Error(`Invalid siteId format: ${siteId}. Expected UUID format.`);
      }

      // Validate file exists
      if (!file || file.size === 0) {
        throw new Error('No valid file selected');
      }

      // Get original image dimensions before upload
      const originalDimensions = await getImageDimensions(file);

      // Storage dimensions: 3x the max display size (320×96) for retina support.
      // Display sizing is handled separately via imageWidth/imageHeight in the component.
      const STORAGE_MAX_WIDTH = 960;
      const STORAGE_MAX_HEIGHT = 480;

      // Upload using the specific navbar logo endpoint
      const uploadResult = await uploadApi.uploadNavbarLogo(file, siteId, {
        alt: logoData.text || file.name,
        maxWidth: STORAGE_MAX_WIDTH,
        maxHeight: STORAGE_MAX_HEIGHT,
        quality: 90,
      });

      // Image upload always uses standard image caps (editor tab is image-only).
      const caps = getLogoDimensionCapsForMode('image');
      const initialDimensions = calculateLogoDimensions(
        originalDimensions.width,
        originalDimensions.height,
        48,
        true,
        caps
      );

      // Update logo data with the uploaded image and dimensions
      onSuccess({
        imageUrl: uploadResult.url,
        cloudfrontUrl: uploadResult.cloudfrontUrl,
        imageAlt: uploadResult.alt,
        mediaId: uploadResult.mediaId,
        s3Key: uploadResult.s3Key,
        originalWidth: originalDimensions.width,
        originalHeight: originalDimensions.height,
        imageWidth: initialDimensions.width,
        imageHeight: initialDimensions.height,
        lockAspectRatio: true,
      });
    } catch (error: any) {
      console.error('Logo upload failed:', error);
      console.error('Full error object:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
      });

      let errorMessage = 'Upload failed. Please try again.';

      if (error.response?.status === 404) {
        errorMessage = 'Upload endpoint not found. Please verify the backend configuration.';
      } else if (error.response?.status === 401) {
        errorMessage = 'Authentication required. Please log in again.';
      } else if (error.response?.status === 400) {
        const backendError = error.response?.data?.error;
        const backendDetails = error.response?.data?.details;
        console.error('Backend validation error details:', backendDetails);

        if (backendError) {
          errorMessage = `Validation error: ${backendError}`;
          if (backendDetails) {
            errorMessage += ` (${JSON.stringify(backendDetails)})`;
          }
        } else {
          errorMessage = 'Invalid file or missing required information.';
        }
      } else if (error.response?.status === 413) {
        errorMessage = 'File too large. Please choose a smaller image.';
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setUploadError(errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  const clearError = () => setUploadError(null);

  return {
    isUploading,
    uploadError,
    fileInputRef,
    handleFileSelect,
    setUploadError,
    clearError,
  };
}
